import React, { FC } from 'react';
import { connect } from 'react-redux';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import {
  DETAILS_SCREENS_CONTAINER_STYLES,
  DETAILS_SCREENS_CONTAINER_STYLES_FOR_SCROLL_VIEW,
  IMAGES_PANEL_VIEW_MODE_WIDTH,
  PANEL_VIEW_MODE_WIDTH,
  pluralTitleRegExp,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
} from '../../constants/base.constants';
import { ScrollView, View } from 'react-native';
import BatchListContent from '@v2/pages/common/components/BatchListContent/BatchListContent';
import { isPhone, isWeb } from '@helpers';
import { ProductionBatchStatistics } from '@interfaces/search';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { scale } from '@components/frame';
import RunningBatch from '@v2/pages/machines/common/components/RunningBatch/RunningBatch';
import { loadSingleBatch } from '@reducers/batch';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import Loading from '@components/loading';
import { GetBatchesByEntityId, LoadSingleBatch } from '@interfaces/methods';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { useGetHeaderConfig } from '@v2/pages/common/hooks/useGetHeaderConfig';
import { TileProps } from '@v2/context/ProductionBatch/types';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface SingleBatchOrBatchListProps extends Pick<TileProps,'withoutImage'> {
  fetchBatchList?: GetBatchesByEntityId;
  idForGettingBatchList?: string | number;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
  batchLoadedTime: string;
  loadSingleBatch: LoadSingleBatch;
  ncFileId?: number;
  batchId?: number;
  displaySingleItem?: boolean;
}

const SingleBatchOrBatchListComponent: FC<SingleBatchOrBatchListProps> = ({
  fetchBatchList,
  idForGettingBatchList,
  batch,
  loadSingleBatch,
  batchLoadedTime,
  ncFileId,
  batchId,
  displaySingleItem,
  withoutImage,
}) => {
  const headerConfig = useGetHeaderConfig();
  
  const shouldDisplayBatchList = displaySingleItem === true ? false : pluralTitleRegExp.test(headerConfig.batch.toString());

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  if (!shouldDisplayBatchList && !batch.wasLoaded) {
    return (
      <View testID={TEST_IDS.runningBatch.loading} style={{ marginTop: scale(40) }}>
        <Loading background="transparent" />
      </View>
    );
  }

  const onBatchImageClick = (selectedImageKey: string) => {
    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.BATCH,
        batchNumber: batch?.data?.batchNumber,
        batchId: batch?.data?.productionBatchId,
        ncFileStatus: batch?.data?.ncFileStatus,
        selectedImageKey,
      },
    });
  };

  return (
    <>
      {shouldDisplayBatchList && (
        <View style={DETAILS_SCREENS_CONTAINER_STYLES}>
          <BatchListContent
            noMachine
            noSearchScreen
            withoutImage={withoutImage}
            {...(fetchBatchList ? { fetchBatchList } : {})}
            {...(ncFileId ? { ncFileId } : {})}
            idForGettingBatchList={idForGettingBatchList}
            backgroundColorForTile={!isWeb ? 'Background/Light/Field on Panel' : 'Background/Light/Field'}
          />
        </View>
      )}
      {!shouldDisplayBatchList && batch.data?.productionBatchId && (
        <View
          style={{
            flexDirection: 'row',
            maxWidth: scale(PANEL_VIEW_MODE_WIDTH),
            marginInline: 'auto',
            width: '100%',
            flex: 1,
          }}
        >
          <ScrollView contentContainerStyle={DETAILS_SCREENS_CONTAINER_STYLES_FOR_SCROLL_VIEW} showsVerticalScrollIndicator={false}>
            <RunningBatch
              loadBatch={() => loadSingleBatch(batch?.data?.productionBatchId)}
              batchLoadedTime={batchLoadedTime}
              batchLoading={!batch.wasLoaded}
              noMachine
              maxDetailsWidth={PANEL_VIEW_MODE_WIDTH - IMAGES_PANEL_VIEW_MODE_WIDTH}
              withImages={isPhone}
              currentBatchId={batchId ?? batch.data?.productionBatchId}
              bg={!isWeb ? 'Background/Light/Field on Panel' : 'Background/Light/Field'}
            />
          </ScrollView>

          {!batch.wasLoaded && !isPhone && <View style={{ width: IMAGES_PANEL_VIEW_MODE_WIDTH, paddingLeft: scale(18), height: '100%' }} />}
          {batch.wasLoaded && !isPhone && (
            <View style={{ paddingTop: scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM) }}>
              <ImagesContainer
                panelViewMode
                mode={ImagesContainerMode.COLUMN}
                documentRetrieveKey={DOCUMENT_ENTITY_TYPE.Batch}
                documentRetrieveId={batch?.data?.productionBatchId}
                onImagePress={onBatchImageClick}
              />
            </View>
          )}
        </View>
      )}
    </>
  );
};

const SingleBatchOrBatchList = connect(
  ({ batch: { batch, batchLoadedTime } }) => ({ batch, batchLoadedTime }),
  { loadSingleBatch }
)(SingleBatchOrBatchListComponent);

export { SingleBatchOrBatchList };
